import { getCenterFromElement } from "../utils/getCenterFromElement";
import { fetchByPos, fetchByLocalPos } from "../utils/fetchByPos";
import { WORD_DIRECTIONS, SOPA_SOUNDS } from "../utils/consts";
import { getDirectionFromTiles } from "../utils/getDirectionFromTiles";
import { tileDistance } from "../utils/tileDistance";
import {
  calculateToleranceZones,
  getToleranceZone,
  constrainToGraceZone
} from "../utils/toleranceCalculator";
import { findClosestTile, findClosestTileBruteForce } from "../utils/findClosestTile";
import { useGameSounds } from "@educaplay/core";
import { useRef } from "react";

/**
 * Hook para manejar los controles de puntero (mouse/touch)
 */
export const usePointerControls = ({
  tiles,
  gridSize,
  gridRef,
  tileRefs,
  activeSelector,
  setActiveSelector
}) => {

  const gameSound = useGameSounds();
  const lastHoverTileId = useRef(null);

  const pointerDownHandler = (x, y) => {

    const gridRect = gridRef?.current?.getBoundingClientRect();
    const tileId = fetchByPos(x, y, gridRect, gridSize);

    if (tileId === null) return;

    const item = tiles[tileId];
    const center = getCenterFromElement(tileRefs.current[item.id]);

    gameSound.play(SOPA_SOUNDS.CARGAR_LETRA);
    
    lastHoverTileId.current = null;

    setActiveSelector({
      origin: item,
      center: center,
      destination: item,
      target: center,
      direction: WORD_DIRECTIONS.NEUTRAL,
      state: 0,
    });
  };

  const pointerLeaveHandler = () => {
    lastHoverTileId.current = null;
  };

  const pointerMoveHandler = (x, y) => {
    if (!activeSelector) return;

    if (typeof x !== 'number' || typeof y !== 'number' ||
      !isFinite(x) || !isFinite(y) ||
      x < 0 || y < 0) {
      return;
    }

    const localSelection = { ...activeSelector };
    const gridRect = gridRef.current?.getBoundingClientRect();

    if (!gridRect) return;

    if (!gridRect || !isFinite(gridRect.x) || !isFinite(gridRect.y) ||
      !isFinite(gridRect.width) || !isFinite(gridRect.height)) {
      return;
    }

    const relativeX = x - gridRect.x;
    const relativeY = y - gridRect.y;

    if (!isFinite(relativeX) || !isFinite(relativeY)) {
      return;
    }

    const toleranceZones = calculateToleranceZones(gridRect, gridSize);
    if (!toleranceZones) {
      return;
    }

    const currentZone = getToleranceZone(relativeX, relativeY, gridRect, toleranceZones);

    if (currentZone === "outside") {
      setActiveSelector(null);
      return;
    }

    let constrainedX = relativeX;
    let constrainedY = relativeY;

    if (currentZone === "grace" || currentZone === "elimination") {
      const constrained = constrainToGraceZone(relativeX, relativeY, gridRect, toleranceZones);
      constrainedX = constrained.x;
      constrainedY = constrained.y;
    }

    const hoveredTileId = fetchByLocalPos(constrainedX, constrainedY, gridRect, gridSize);
    let hoveredItem = null;

    if (hoveredTileId !== null) {
      hoveredItem = tiles[hoveredTileId];
    } else {
      hoveredItem = findClosestTile(constrainedX, constrainedY, gridRect, gridSize, tiles);

      if (!hoveredItem) {
        hoveredItem = findClosestTileBruteForce(
          constrainedX,
          constrainedY,
          gridRect,
          gridSize,
          tiles,
          tileRefs
        );
      }
    }

    if (!hoveredItem) {
      if (isFinite(constrainedX) && isFinite(constrainedY)) {
        localSelection.target = {
          x: constrainedX,
          y: constrainedY,
        };
        setActiveSelector(localSelection);
      }
      return;
    }

    const logicDistance = tileDistance(localSelection.origin, hoveredItem);

    if (logicDistance < 1) {
      const dir = getDirectionFromTiles(localSelection.origin, hoveredItem);
      const target = getCenterFromElement(tileRefs.current[hoveredItem.id]);

      localSelection.destination = hoveredItem;
      localSelection.direction = dir;
      localSelection.target = target;
    } else {
      if (logicDistance < 2) {
        const dir = getDirectionFromTiles(localSelection.origin, hoveredItem);
        localSelection.direction = dir;
      }

      if (localSelection.direction !== WORD_DIRECTIONS.NEUTRAL) {
        if (localSelection.direction.x === 0) {
          const deltaY = constrainedY - localSelection.center.y;
          const directionSign = localSelection.direction.y;
          if ((directionSign > 0 && deltaY >= 0) || (directionSign < 0 && deltaY <= 0)) {
            if (!isFinite(localSelection.center.x) || !isFinite(constrainedY)) {
              return;
            }
            localSelection.target = {
              x: localSelection.center.x,
              y: constrainedY,
            };
          } else {
            localSelection.target = {
              x: localSelection.center.x,
              y: localSelection.center.y,
            };
          }
        } else if (localSelection.direction.y === 0) {
          const deltaX = constrainedX - localSelection.center.x;
          const directionSign = localSelection.direction.x;

          if ((directionSign > 0 && deltaX >= 0) || (directionSign < 0 && deltaX <= 0)) {
            if (!isFinite(constrainedX) || !isFinite(localSelection.center.y)) {
              return;
            }
            localSelection.target = {
              x: constrainedX,
              y: localSelection.center.y,
            };
          } else {
            localSelection.target = {
              x: localSelection.center.x,
              y: localSelection.center.y,
            };
          }
        } else {
          let distancia = 0;
          const direction = localSelection.direction;

          if (direction.x === 1 && direction.y === -1) { // NE
            const deltaY = localSelection.center.y - constrainedY;
            distancia = deltaY < 0 ? 0 : deltaY;
          } else if (direction.x === -1 && direction.y === -1) { // NO  
            const deltaY = localSelection.center.y - constrainedY;
            distancia = deltaY < 0 ? 0 : deltaY;
          } else if (direction.x === 1 && direction.y === 1) { // SE
            const deltaY = constrainedY - localSelection.center.y;
            distancia = deltaY < 0 ? 0 : deltaY;
          } else if (direction.x === -1 && direction.y === 1) { // SO
            const deltaY = constrainedY - localSelection.center.y;
            distancia = deltaY < 0 ? 0 : deltaY;
          }

          let targetX = localSelection.center.x;
          let targetY = localSelection.center.y;

          if (distancia > 0) {
            if (direction.x === 1 && direction.y === -1) { // NE
              targetX = localSelection.center.x + distancia;
              targetY = localSelection.center.y - distancia;
            } else if (direction.x === -1 && direction.y === -1) { // NO
              targetX = localSelection.center.x - distancia;
              targetY = localSelection.center.y - distancia;
            } else if (direction.x === 1 && direction.y === 1) { // SE
              targetX = localSelection.center.x + distancia;
              targetY = localSelection.center.y + distancia;
            } else if (direction.x === -1 && direction.y === 1) { // SO
              targetX = localSelection.center.x - distancia;
              targetY = localSelection.center.y + distancia;
            }
          }

          const tileWidth = gridRect.width / gridSize;
          const tileHeight = gridRect.height / gridSize;

          const gridMinX = tileWidth / 2;
          const gridMaxX = gridRect.width - (tileWidth / 2);
          const gridMinY = tileHeight / 2;
          const gridMaxY = gridRect.height - (tileHeight / 2);


          targetX = Math.max(gridMinX, Math.min(gridMaxX, targetX));
          targetY = Math.max(gridMinY, Math.min(gridMaxY, targetY));

          localSelection.target = {
            x: targetX,
            y: targetY,
          };
        }
      }

      const finalTileId = fetchByLocalPos(
        localSelection.target.x,
        localSelection.target.y,
        gridRect,
        gridSize
      );

      if (finalTileId !== null) {
        localSelection.destination = tiles[finalTileId];
      } else {
        const closestTile = findClosestTile(
          localSelection.target.x,
          localSelection.target.y,
          gridRect,
          gridSize,
          tiles
        );
        if (closestTile) {
          localSelection.destination = closestTile;
        }
      }
    }

    if (activeSelector && hoveredItem &&
        hoveredItem.id !== activeSelector.destination?.id &&
        hoveredItem.id !== activeSelector.origin?.id &&
        hoveredItem.id !== lastHoverTileId.current) {
      gameSound.play(SOPA_SOUNDS.MOUSE_HOVER);
      lastHoverTileId.current = hoveredItem.id;
    }

    setActiveSelector(localSelection);
  };

  return {
    pointerDownHandler,
    pointerLeaveHandler,
    pointerMoveHandler
  };
};
